import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { BASE } from '../api/client';
import {
  useBackgrounds,
  useUploadBackground,
  type BackgroundAsset,
} from '../hooks/useApi';

/**
 * Página `/backgrounds` — galería global de fondos / escenarios / imágenes 2D
 * reutilizables. El operador puede:
 *  - Ver TODOS los fondos disponibles (los que subió globalmente + los que
 *    subió a proyectos específicos), agrupados visualmente.
 *  - Subir nuevos sin obligación de elegir proyecto (van al proyecto interno
 *    `__backgrounds-gallery`).
 *  - Filtrar por cliente / proyecto / texto libre.
 *  - Click en thumb → lightbox tamaño completo (B.3).
 */
export function BackgroundsPage() {
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [lightbox, setLightbox] = useState<BackgroundAsset | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: backgrounds, isLoading, error } = useBackgrounds({
    q: search || undefined,
    client: clientFilter || undefined,
  });
  const upload = useUploadBackground();

  // Lista única de clientes para el dropdown de filtro.
  const clients = useMemo(() => {
    if (!backgrounds) return [];
    const set = new Set<string>();
    for (const b of backgrounds) {
      if (b.projectClient) set.add(b.projectClient);
    }
    return Array.from(set).sort();
  }, [backgrounds]);

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await upload.mutateAsync(file);
      e.target.value = ''; // permite resubir el mismo archivo si quiere
    } catch (err) {
      alert(`Upload falló: ${err instanceof Error ? err.message : err}`);
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-slate-100">🖼 Galería de Fondos</h1>
          <Link to="/projects" className="text-xs text-slate-400 hover:text-vision underline">
            ← volver a proyectos
          </Link>
        </div>
        <p className="text-sm text-slate-400">
          Catálogo global de fondos / escenarios / imágenes 2D reutilizables. Subí acá
          fondos que vas a usar en lipsync composite, environments y referencias visuales —
          quedan disponibles en cualquier proyecto.
        </p>
      </header>

      <section className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] text-slate-500 uppercase block mb-1">Buscar</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="texto libre, descripción, proyecto..."
            className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-vision"
          />
        </div>

        {clients.length > 0 && (
          <div className="min-w-[160px]">
            <label className="text-[10px] text-slate-500 uppercase block mb-1">Cliente</label>
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
            >
              <option value="">Todos</option>
              {clients.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">
            {backgrounds?.length ?? 0} fondo{backgrounds?.length === 1 ? '' : 's'}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onFileSelected}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={upload.isPending}
            className="px-3 py-1.5 text-sm rounded bg-vision-dark hover:bg-vision text-white disabled:opacity-40"
          >
            {upload.isPending ? '⬆ Subiendo…' : '+ Subir fondo'}
          </button>
        </div>
      </section>

      {isLoading && <div className="text-slate-400 text-sm">Cargando…</div>}

      {error && (
        <div className="text-red-400 text-sm">
          Error: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {backgrounds && backgrounds.length === 0 && (
        <div className="text-center text-slate-500 py-12 text-sm">
          La galería está vacía.
          <br />
          Apretá <strong>+ Subir fondo</strong> para agregar el primero.
        </div>
      )}

      {backgrounds && backgrounds.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {backgrounds.map((bg) => (
            <BackgroundCard key={bg.id} bg={bg} onClick={() => setLightbox(bg)} />
          ))}
        </div>
      )}

      {lightbox && <Lightbox bg={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function BackgroundCard({ bg, onClick }: { bg: BackgroundAsset; onClick: () => void }) {
  const thumbUrl = `${BASE}/assets/${bg.id}/thumb`;
  const proj = bg.projectName === '🖼 Galería de Fondos (sistema)' ? 'global' : bg.projectName;
  return (
    <button
      onClick={onClick}
      className="group block bg-slate-900 border border-slate-700 hover:border-vision rounded-lg overflow-hidden text-left transition-colors"
    >
      <div className="aspect-video bg-slate-950 overflow-hidden flex items-center justify-center">
        <img
          src={thumbUrl}
          alt=""
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
      <div className="px-2 py-1.5 text-[10px] text-slate-400">
        <div className="truncate" title={proj}>
          {proj === 'global' ? <span className="text-emerald-400">🌐 global</span> : `📁 ${proj}`}
        </div>
        {bg.width && bg.height && (
          <div className="text-slate-600 text-[9px]">
            {bg.width}×{bg.height}
          </div>
        )}
      </div>
    </button>
  );
}

function Lightbox({ bg, onClose }: { bg: BackgroundAsset; onClose: () => void }) {
  const fullUrl = `${BASE}/assets/${bg.id}/proxy`;
  const shortRef = bg.metadata?.shortRef;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
          <div className="text-xs text-slate-300">
            <strong>{bg.projectName}</strong>
            {bg.projectClient && <span className="text-slate-500"> · {bg.projectClient}</span>}
            {bg.width && bg.height && (
              <span className="text-slate-600 ml-2">({bg.width}×{bg.height})</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {shortRef && (
              <button
                onClick={() => navigator.clipboard.writeText(`#${shortRef}`)}
                className="text-[10px] px-2 py-1 rounded bg-vision/20 hover:bg-vision/40 text-vision border border-vision/40"
                title="Copiar handle para usar en chat"
              >
                📋 #{shortRef}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-200 text-lg leading-none px-2"
            >
              ✕
            </button>
          </div>
        </div>
        <img
          src={fullUrl}
          alt=""
          className="w-full h-auto max-h-[85vh] object-contain rounded border border-slate-700"
        />
      </div>
    </div>
  );
}
